import {
  FormatType,
  numberRemoveSeparator,
  numberSeparator,
} from "@/lib/helper";
import { persianToEnglish } from "@/lib/helper/persian-to-english";
import type { ComponentProps } from "react";
import InputWithLabel, {
  InputWithLabelProps,
} from "./input-with-label.component";
import { Input } from "@/components/ui/input";

// Base formatting props
interface BaseFormattingProps {
  groupSizeSeparate?: number;
  separator?: string;
  formatType?: FormatType;
  onChange?: (value: string) => void;
  value?: string | number;
}

type NormalInputProps = BaseFormattingProps &
  Omit<ComponentProps<"input">, keyof BaseFormattingProps> & {
    label?: never;
  };

type LabeledInputProps = BaseFormattingProps &
  Omit<InputWithLabelProps, keyof BaseFormattingProps> & {
    label: string;
  };

const FormattedNumberInput = (props: NormalInputProps | LabeledInputProps) => {
  const {
    groupSizeSeparate,
    separator,
    value,
    formatType,
    onChange,
    label,
    ...restProps
  } = props;

  const formattedValue = formatType
    ? numberSeparator({
        value: String(value ?? ""),
        type: formatType,
        groupSize: groupSizeSeparate,
        separator,
      })
    : value;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = numberRemoveSeparator(persianToEnglish(e.target.value));
    onChange?.(rawValue);
  };

  if (label) {
    return (
      <InputWithLabel
        {...(restProps as Omit<
          LabeledInputProps,
          keyof BaseFormattingProps | "label"
        >)}
        label={label}
        value={formattedValue}
        onChange={handleChange}
      />
    );
  }

  return (
    <Input
      {...(restProps as Omit<
        NormalInputProps,
        keyof BaseFormattingProps | "label"
      >)}
      value={formattedValue}
      onChange={handleChange}
    />
  );
};

export default FormattedNumberInput;
export type { NormalInputProps, LabeledInputProps };
